import {
    Component,
    h,
    Prop,
    ComponentInterface,
    Host,
    Element,
} from "@stencil/core";
import { ButtonLabelVisibility } from "../../utils/buttonLabelVisibility";

export type ButtonVariant = "default" | "primary" | "no-border";

export type ButtonSize = "default" | "icon" | "icon-stretched";

/**
 * @slot (default) - Content is placed inside the button if provided without a slot name
 * @slot "icon" - Should be used to place a custom icon
 */
@Component({
    tag: "ux-button",
    styleUrl: "button.scss",
})
export class Button implements ComponentInterface {
    @Element() host: HTMLUxButtonElement;

    /** The type attribute of the button element. */
    @Prop() type?: "button" | "submit" | "reset";

    /** The name attribute of the button element. */
    @Prop({ reflect: true }) name?: string;

    /** If set to `true`, the user cannot interact with the button. */
    @Prop({ reflect: true }) disabled = false;

    /** If set to `true`, the button can be toggled selected. */
    @Prop() toggle = false;

    /** If set to `true` in toggle mode, the button is selected. */
    @Prop({ reflect: true }) selected = false;

    /** The size of the button. Use `icon` for a button with just an icon. */
    @Prop() size: ButtonSize = "default";

    /** Label text of the button, required for better accessibility */
    @Prop() label?: string;

    /** If set to `true`, the label is not truncated. */
    @Prop() multiline = false;

    /** The name of the icon to show next to the label. */
    @Prop() icon?: string;

    /** Horizontal placement of the icon. */
    @Prop() iconPlacement: "start" | "end" = "start";

    /** Breakpoint when label should be visible */
    @Prop() labelVisibility: ButtonLabelVisibility = "default";

    /** Style variant of the button. Note that not all variants are supported by every theme. */
    @Prop() variant: ButtonVariant = "default";

    private getTabIndex = (): number => {
        if (this.disabled) {
            return -1;
        }
        const tabindexAttr = this.host.getAttribute("tabindex");
        if (tabindexAttr !== null && !Number.isNaN(parseInt(tabindexAttr))) {
            return parseInt(tabindexAttr);
        }
        return 0;
    };

    render() {
        const baseClass = "ux-button";
        const hasCustomIcon = this.host.querySelector("[slot=icon]") !== null;
        const hasIcon = hasCustomIcon || (this.icon && this.icon.length > 0);
        return (
            <Host>
                <button
                    class={{
                        [baseClass]: true,
                        [`${baseClass}--${this.variant || "default"}`]: true,
                        [`${baseClass}--icon-only`]: this.size === "icon",
                        [`${baseClass}--icon-placement-${this.iconPlacement}`]:
                            hasIcon && this.size !== "icon",
                        [`${baseClass}__label-visibility ${baseClass}__label-visibility--${this.labelVisibility}`]:
                            this.labelVisibility !== "default",
                        [`${baseClass}--disabled`]: this.disabled,
                        [`${baseClass}--selected`]: this.selected,
                        [`${baseClass}--toggle`]: this.toggle,
                        [`${baseClass}--toggle-active`]:
                            this.toggle && this.selected,
                    }}
                    tabIndex={this.getTabIndex()}
                    type={this.type}
                    name={this.name}
                    disabled={this.disabled}
                    aria-label={this.label}
                >
                    <span
                        class={{
                            [`${baseClass}__icon`]: hasIcon,
                            [`${baseClass}__icon--none`]: !hasIcon,
                            [`${baseClass}__icon--custom`]: hasCustomIcon,
                        }}
                    >
                        <slot name="icon">
                            {this.icon && <ux-icon name={this.icon} />}
                        </slot>
                    </span>
                    <span
                        class={{
                            [`${baseClass}__label`]: true,
                            [`${baseClass}__label--truncate`]: !this.multiline,
                            [`${baseClass}__label--hidden`]:
                                this.size === "icon",
                        }}
                    >
                        <slot>{this.label}</slot>
                    </span>
                </button>
            </Host>
        );
    }
}
